import React, { useEffect, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { hasActiveSession, hasStoredSessionTokens, isSilentRenewDisabled, renewSessionSilently, shouldRenewSessionSoon } from './session';

const SESSION_RENEW_INTERVAL_MS = 15000;
const requireSessionRenewBeforeMount = () => !isSilentRenewDisabled() && hasStoredSessionTokens() && shouldRenewSessionSoon();

const ProtectedRoute = ({ component: Component, ...rest }) => {
    const [isSessionResolved, setIsSessionResolved] = useState(() => {
        if (hasActiveSession()) {
            return true;
        }

        return hasStoredSessionTokens() && isSilentRenewDisabled();
    });
    const [isCheckingSession, setIsCheckingSession] = useState(() => hasStoredSessionTokens() && !hasActiveSession() && !isSilentRenewDisabled());

    useEffect(() => {
        let active = true;

        if (!hasStoredSessionTokens()) {
            setIsSessionResolved(false);
            setIsCheckingSession(false);
            return () => {
                active = false;
            };
        }

        if (hasActiveSession() && !requireSessionRenewBeforeMount()) {
            setIsSessionResolved(true);
            setIsCheckingSession(false);
            return () => {
                active = false;
            };
        }

        if (isSilentRenewDisabled() && hasActiveSession()) {
            setIsSessionResolved(true);
            setIsCheckingSession(false);
            return () => {
                active = false;
            };
        }

        setIsCheckingSession(true);
        setIsSessionResolved(false);

        renewSessionSilently()
            .then(() => {
                if (!active) {
                    return;
                }

                setIsSessionResolved(hasActiveSession());
                setIsCheckingSession(false);
            })
            .catch(() => {
                if (!active) {
                    return;
                }

                setIsSessionResolved(hasActiveSession());
                setIsCheckingSession(false);
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let active = true;

        const runSilentRenewIfNeeded = () => {
            if (!active || !hasStoredSessionTokens() || isSilentRenewDisabled()) {
                return;
            }

            if (!shouldRenewSessionSoon()) {
                return;
            }

            renewSessionSilently()
                .then(() => {
                    if (!active) {
                        return;
                    }

                    setIsSessionResolved(hasActiveSession());
                    setIsCheckingSession(false);
                })
                .catch(() => {
                    if (!active) {
                        return;
                    }

                    setIsSessionResolved(hasActiveSession());
                    setIsCheckingSession(false);
                });
        };

        runSilentRenewIfNeeded();
        const intervalId = window.setInterval(runSilentRenewIfNeeded, SESSION_RENEW_INTERVAL_MS);

        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, []);

    return (
        <Route
            {...rest}
            render={props => {
                if (isCheckingSession) {
                    return null;
                }

                return isSessionResolved ? (
                    <Component {...props} />
                ) : (
                    <Redirect
                        to={{
                            pathname: '/logout',
                            state: {
                                from: props.location,
                                reason: 'session_expired'
                            }
                        }}
                    />
                );
            }}
        />
    );
};

export default ProtectedRoute;